import { Injectable, inject } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { filter } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class TranslationService {
  private translateService = inject(TranslateService);
  private router = inject(Router);
  private currentLanguage: 'en' | 'mr' | 'hi';

  constructor() {
    // Initialize current language from localStorage or default to 'en'
    const savedLang = localStorage.getItem('preferredLanguage') || 'en';
    this.currentLanguage = (savedLang === 'en' || savedLang === 'mr' || savedLang === 'hi') 
      ? savedLang as 'en' | 'mr' | 'hi' 
      : 'en';
    
    // Listen to route changes and disable translation for admin routes
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        const url = event.urlAfterRedirects || event.url;
        if (url.startsWith('/admin')) {
          // Disable translation for admin routes
          this.translateService.setDefaultLang('en');
        } else {
          // Enable translation for non-admin routes
          this.setLanguage(this.currentLanguage);
        }
      });
  }

  /**
   * Initialize translation service
   */
  init(): void {
    // Set default language
    const savedLang = localStorage.getItem('preferredLanguage') || 'en';
    this.translateService.setDefaultLang('en');
    this.setLanguage(savedLang as 'en' | 'mr' | 'hi');
  }

  /**
   * Set the current language
   */
  setLanguage(lang: 'en' | 'mr' | 'hi'): void {
    this.currentLanguage = lang;
    localStorage.setItem('preferredLanguage', lang);
    this.translateService.use(lang);
  }

  /**
   * Get current language
   */
  getCurrentLanguage(): string {
    return this.currentLanguage;
  }

  /**
   * Check if current route is admin route
   */
  isAdminRoute(): boolean {
    return this.router.url.startsWith('/admin');
  }

  /**
   * Translate a key
   */
  translate(key: string, params?: any): string {
    if (this.isAdminRoute()) {
      // Return key as-is for admin routes
      return key;
    }
    return this.translateService.instant(key, params);
  }
}

